<script setup>
import { ref, computed, onMounted } from 'vue';
import { i18n } from '@/i18n';

const props = defineProps({
  defaultShow: {
    type: Boolean,
    default: false,
  },
  vip: {
    type: Boolean,
    default: false,
  },
  show: {
    type: Boolean,
    default: true,
  },
  delay: {
    type: Number,
    default: 3000,
  },
});

const emit = defineEmits(['close']);
const showWeChatPopup = ref(false);
const isChineseLanguage = computed(() => i18n.isChinese);

defineExpose({ open });

function open() {
  showWeChatPopup.value = true;
}

const cacheKey = 'hasShownWechatPopup';

onMounted(() => {
  if (props.defaultShow) {
    showWeChatPopup.value = true;
    return;
  }
  const hasShownWechatPopup = localStorage.getItem(cacheKey);
  if (props.show && isChineseLanguage.value && !hasShownWechatPopup) {
    setTimeout(() => {
      showWeChatPopup.value = true;
    }, props.delay);
  }
});

const closeWeChatPopup = () => {
  showWeChatPopup.value = false;
  localStorage.setItem(cacheKey, 'true');
  emit('close');
};

const groupTypes = {
  default: {
    title: '加入 Seede AI 微信交流群',
    qrcode: '/images/wechat-group-qrcode.webp?v=10',
    description: '获取最新产品资讯和技术支持',
  },
  vip: {
    title: '加入 Seede AI 核心用户群',
    qrcode: '/images/seede-wechat-vip-group.webp?v=4',
    description: '扫描二维码加入核心用户社区 <br />优先获取产品更新与专业技术支持',
  },
};

const group = computed(() => (props.vip ? groupTypes.vip : groupTypes.default));
</script>

<template>
  <Transition name="popup">
    <div v-if="showWeChatPopup" class="fixed bottom-4 right-4 z-50">
      <div
        :class="[
          'popup-blur-container px-10 py-6 rounded-[24px] border shadow-[0_10px_20px_0px_rgba(0,0,0,0.1)] max-w-[320px] relative overflow-hidden',
          props.vip ? 'border-white/60' : 'border-black/10',
        ]"
      >
        <button
          @click="closeWeChatPopup"
          class="absolute top-2 right-2 text-black/40 hover:text-black p-1 rounded-full"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-4 w-4"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fill-rule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clip-rule="evenodd"
            />
          </svg>
        </button>
        <div class="flex flex-col items-center text-center">
          <div class="flex items-center justify-center mb-2">
            <img src="/images/wechat.png" class="h-10 w-10" alt="WeChat" />
          </div>
          <h3 class="text-sm font-normal text-black/80 mb-0.5">
            {{ group.title }}
          </h3>
          <p
            :class="['text-xs mb-4 leading-relaxed', props.vip ? 'text-black/50' : 'text-black/40']"
            v-html="group.description"
          ></p>
          <div
            :class="[
              'relative overflow-hidden',
              props.vip ? 'border-black/30 ring-1 ring-black/10' : 'border-black/10',
            ]"
          >
            <img
              :src="group.qrcode"
              alt="WeChat QR Code"
              class="w-24 h-24 object-cover relative z-10"
            />
            <div
              v-if="props.vip"
              class="absolute top-0 right-0 z-[100] text-black text-xs px-2 py-0.5 rounded-bl-lg font-medium"
            >
              VIP
            </div>
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
/* 模糊背景容器 - 使用伪元素实现可靠的 blur 效果 */
.popup-blur-container {
  background: rgba(245, 244, 242, 0.7);
}

.popup-blur-container::before {
  content: '';
  position: absolute;
  inset: 0;
  backdrop-filter: blur(60px);
  -webkit-backdrop-filter: blur(60px);
  z-index: -1;
  border-radius: inherit;
}

/* Vue Transition 动画 */
.popup-enter-active {
  animation: popup-in 0.4s ease-out;
}

.popup-leave-active {
  animation: popup-out 0.3s ease-in;
}

@keyframes popup-in {
  0% {
    opacity: 0;
    transform: translateY(20px) scale(0.95);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes popup-out {
  0% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
  100% {
    opacity: 0;
    transform: translateY(10px) scale(0.98);
  }
}
</style>
